import React, { useState, useEffect } from "react";
import { Card, Row, Col, message, Input, Button, Table, Spin, Tag, Tooltip, Modal, Typography } from "antd";
import Title from "antd/lib/typography/Title";
import { SearchOutlined, InfoCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { NavLink } from "react-router-dom";
import axios from "axios";
import ReservationForm from "./reservationForm";

const { Search } = Input;

const ReservationsList = () => {
  const [reservations, setReservations] = useState([]);
  const [filteredReservations, setFilteredReservations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    fetchReservations();
  }, []);

  const fetchReservations = async () => {
    try {
      setLoading(true);
      const response = await axios.get("http://93.127.203.113:5005/reservations");
      setReservations(response.data);
      setFilteredReservations(response.data);
    } catch (error) {
      message.error("Unable to load reservations!");
    } finally {
      setLoading(false);
    }
  };

  const handleGlobalSearch = (value) => {
    setSearchText(value);
    const lowerSearch = value.toLowerCase();
    const filtered = reservations.filter((record) => {
      const fieldsToSearch = [
        record.id,
        `${record.passenger?.firstName || ""} ${record.passenger?.lastName || ""}`,
        record.vehicle?.plateNumber || "",
        record.vehicle?.model || "",
        record.customer_agreement?.agreementId || "",
        record.price,
        record.status,
        new Date(record.startDate).toLocaleDateString("en-US"),
        new Date(record.endDate).toLocaleDateString("en-US"),
      ];
      return fieldsToSearch.some((field) =>
        field?.toString().toLowerCase().includes(lowerSearch)
      );
    });
    setFilteredReservations(filtered);
  };

  const highlightText = (text, searchText) => {
    if (!searchText) return text;
    const regex = new RegExp(`(${searchText})`, "gi");
    const parts = text.toString().split(regex);

    return parts.map((part, index) =>
      regex.test(part) ? (
        <span
          key={index}
          style={{
            backgroundColor: "#f4f7a1", // Light yellow for highlights
            fontWeight: "bold",
          }}
        >
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  const columns = [
    {
      title: <span style={{ fontWeight: "bold", color: "#0c8c49" }}>Reservation ID</span>,
      dataIndex: "id",
      key: "id",
      render: (text) => highlightText(text, searchText),
    },
    {
      title: <span style={{ fontWeight: "bold", color: "#0c8c49" }}>Customer Name</span>,
      key: "passenger",
      render: (record) =>
        record.passenger
          ? highlightText(
              `${record.passenger.firstName} ${record.passenger.lastName}`,
              searchText
            )
          : "N/A",
    },
    {
      title: <span style={{ fontWeight: "bold", color: "#0c8c49" }}>Vehicle</span>,
      key: "vehicle",
      render: (record) =>
        record.vehicle
          ? highlightText(
              `${record.vehicle.plateNumber} (${record.vehicle.model})`,
              searchText
            )
          : "N/A",
    },
    {
      title: <span style={{ fontWeight: "bold", color: "#0c8c49" }}>Agreement Document</span>,
      key: "agreement",
      render: (record) =>
        record.customer_agreement
          ? highlightText(
              `#${record.customer_agreement.agreementId} (${new Date(
                record.customer_agreement.startDate
              ).toLocaleDateString()} - ${new Date(
                record.customer_agreement.endDate
              ).toLocaleDateString()})`,
              searchText
            )
          : "N/A",
    },
    {
      title: <span style={{ fontWeight: "bold", color: "#0c8c49" }}>Price</span>,
      dataIndex: "price",
      key: "price",
      render: (text) => highlightText(`ETB ${text}`, searchText),
    },
    {
      title: <span style={{ fontWeight: "bold", color: "#0c8c49" }}>Start Date</span>,
      dataIndex: "startDate",
      key: "startDate",
      render: (date) =>
        highlightText(
          date ? new Date(date).toLocaleDateString("en-US") : "N/A",
          searchText
        ),
    },
    {
      title: <span style={{ fontWeight: "bold", color: "#0c8c49" }}>End Date</span>,
      dataIndex: "endDate",
      key: "endDate",
      render: (date) =>
        highlightText(
          date ? new Date(date).toLocaleDateString("en-US") : "N/A",
          searchText
        ),
    },
    {
      title: <span style={{ fontWeight: "bold", color: "#0c8c49" }}>Status</span>,
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const color =
          status === "pending"
            ? "orange"
            : status === "confirmed"
            ? "green"
            : status === "cancelled"
            ? "red"
            : "gray";
        return (
          <Tag color={color}>
            {highlightText(status?.toUpperCase() || "N/A", searchText)}
          </Tag>
        );
      },
    },
    {
      title: <span style={{ fontWeight: "bold", color: "#0c8c49" }}>Actions</span>,
      key: "actions",
      render: (_, record) => (
        <Button type="link">
          <NavLink
            to={`/reservationDetails/${record.id}`}
            style={{ color: "#aa0077" }}
          >
            <InfoCircleOutlined /> Details
          </NavLink>
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: "20px" }}>
  <Card
        style={{
          borderRadius: "10px",
          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
          borderTop: "5px solid #0c8c49",
        }}
      >
        <Title level={4} style={{ color: "#0c8c49", marginBottom: "20px" }}>
          Reservations
        </Title>
        <Row
          gutter={[16, 16]}
          style={{ marginBottom: "20px" }}
          align="middle"
          justify="space-between"
        >
          <Col span={18}>
            <Search
              placeholder="Search Reservations"
              allowClear
              enterButton={<SearchOutlined />}
              onSearch={handleGlobalSearch}
              onChange={(e) => handleGlobalSearch(e.target.value)}
              style={{
                width: "60%",
                border: "1px solid #0c8c49",
                borderRadius: "5px",
              }}
            />
          </Col>
          <Col span={6} style={{ textAlign: "right" }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setModalVisible(true)}
              style={{
                backgroundColor: "#0c8c49",
                borderColor: "#0c8c49",
              }}
            >
              Add Reservation
            </Button>
          </Col>
        </Row>

        <Row>
          <Col span={24}>
            {loading ? (
              <div style={{ textAlign: "center", marginTop: "20px" }}>
                <Spin size="large" />
              </div>
            ) : (
              <Table
                columns={columns}
                dataSource={filteredReservations}
                rowKey="id"
                pagination={{ pageSize: 5 }}
                bordered
                style={{ borderColor: "#0c8c49" }}
              />
            )}
          </Col>
        </Row>
      </Card>

      <Modal
        title={<span style={{ color: "#aa0077" }}>Add Reservation</span>}
        visible={modalVisible}
        footer={null}
        onCancel={() => setModalVisible(false)}
        width="50%" // Increased the width of the modal

      >
        <ReservationForm
          formData={{}}
          setFormData={() => {}}
          closeModal={() => setModalVisible(false)}
          refetchData={fetchReservations}
        />
      </Modal>
    </div>
  );
};

export default ReservationsList;
